import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiPhone, FiMail, FiMapPin, FiFacebook, FiInstagram, FiLinkedin, FiTwitter, FiYoutube,
  FiArrowRight, FiCode,
} from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext';

const footerSections = [
  {
    titleKey: 'footer.popularCourses',
    links: [
      { label: 'MBBS in India & Abroad', to: '/medical-counseling' },
      { label: 'Engineering Programs', to: '/engineering-counseling' },
      { label: 'MBA & Management', to: '/management-programs' },
      { label: 'IT & Computer Science', to: '/courses?category=it' },
      { label: 'Nursing & Paramedical', to: '/nursing-programs' },
      { label: 'Study Abroad', to: '/study-abroad' },
    ],
  },
  {
    titleKey: 'footer.studyDestinations',
    links: [
      { label: '🇺🇸 Study in USA', to: '/country/usa' },
      { label: '🇬🇧 Study in UK', to: '/country/uk' },
      { label: '🇨🇦 Study in Canada', to: '/country/canada' },
      { label: '🇦🇺 Study in Australia', to: '/country/australia' },
      { label: '🇩🇪 Study in Germany', to: '/country/germany' },
      { label: '🇸🇬 Study in Singapore', to: '/country/singapore' },
    ],
  },
  {
    titleKey: 'footer.quickLinks',
    links: [
      { label: 'About YouthInfotech', to: '/about' },
      { label: 'Our Counseling Process', to: '/about#process' },
      { label: 'Scholarships', to: '/scholarships' },
      { label: 'Success Stories', to: '/success-stories' },
      { label: 'Blog & Knowledge Center', to: '/blog' },
      { label: 'Book Free Session', to: '/free-counseling' },
    ],
  },
];

const socialLinks = [
  { icon: <FiFacebook />, href: '#', label: 'Facebook', color: 'hover:text-blue-400' },
  { icon: <FiInstagram />, href: '#', label: 'Instagram', color: 'hover:text-pink-400' },
  { icon: <FiLinkedin />, href: '#', label: 'LinkedIn', color: 'hover:text-blue-300' },
  { icon: <FiTwitter />, href: '#', label: 'Twitter', color: 'hover:text-sky-400' },
  { icon: <FiYoutube />, href: '#', label: 'YouTube', color: 'hover:text-red-400' },
];

export default function Footer() {
  const { t } = useLanguage();
  return (
    <footer className="bg-navy-950 text-white">
      {/* CTA Banner */}
      <div className="bg-gold-gradient py-12">
        <div className="container-custom flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">
              {t('cta.title')}
            </h3>
            <p className="text-navy-800 mt-1">
              Get a free counseling session with our expert advisors today.
            </p>
          </div>
          <Link
            to="/free-counseling"
            className="bg-navy-900 hover:bg-navy-800 text-white font-semibold px-8 py-3.5 rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg flex-shrink-0"
          >
            {t('common.bookFreeSession')} <FiArrowRight />
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <div className="w-10 h-10 bg-gold-gradient rounded-xl flex items-center justify-center">
                <span className="text-navy-900 font-heading font-bold text-lg">Y</span>
              </div>
              <div>
                <div className="font-heading font-bold text-white text-lg leading-none">YouthInfotech</div>
                <div className="text-xs text-gray-400 leading-none">Global Education Consultancy</div>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">
              {t('footer.tagline')}
            </p>
            
            {/* Contact */}
            <div className="space-y-3 mb-6">
              <a href="tel:+918310408977" className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-gold-400 transition-colors">
                <FiPhone className="text-gold-500 flex-shrink-0" />
                +91 83104 08977
              </a>
              <a href="mailto:youthinfotech01@gmail.com" className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-gold-400 transition-colors">
                <FiMail className="text-gold-500 flex-shrink-0" />
                youthinfotech01@gmail.com
              </a>
              <div className="flex items-start gap-2.5 text-sm text-gray-400">
                <FiMapPin className="text-gold-500 flex-shrink-0 mt-0.5" />
                123 Education Hub, Connaught Place, New Delhi – 110001, India
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className={`w-9 h-9 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center text-gray-400 ${social.color} transition-all duration-200`}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section) => (
            <div key={section.titleKey}>
              <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">{t(section.titleKey)}</h4>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-gray-400 hover:text-gold-400 transition-colors duration-200 flex items-center gap-1 group"
                    >
                      <span className="group-hover:translate-x-1 transition-transform duration-200">{link.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Certifications / Trust Badges */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-wrap gap-4 justify-center md:justify-start mb-8">
            {[
              '🏆 ISO 9001:2015 Certified',
              '✅ NMC Approved Counselor',
              '🎓 AIRC Member',
              '🌍 ICEF Certified Agent',
              '⭐ 4.9/5 Google Rating',
            ].map((badge) => (
              <span
                key={badge}
                className="text-xs bg-white/10 text-gray-300 px-3 py-1.5 rounded-lg border border-white/10"
              >
                {badge}
              </span>
            ))}
          </div>
          
          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
            <p>© {new Date().getFullYear()} YouthInfotech. {t('footer.rights')} Built with ❤️ for students worldwide.</p>
            <div className="flex gap-4">
              <Link to="/about" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
              <Link to="/about" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
              <Link to="/contact" className="hover:text-gray-300 transition-colors">Sitemap</Link>
            </div>
          </div>

          {/* Developer Credit */}
          <div className="mt-6 pt-5 border-t border-white/5 flex flex-col sm:flex-row items-center justify-center gap-1.5 text-center">
            <span className="flex items-center gap-1.5 text-xs text-gray-600">
              <FiCode size={12} className="text-gold-600" />
              {t('footer.developer')}
            </span>
            <a
              href="mailto:info@trishivax.com"
              className="text-xs text-gold-600 hover:text-gold-400 font-semibold transition-colors"
            >
              {t('footer.devCompany')}
            </a>
            <span className="text-gray-700 text-xs hidden sm:inline">•</span>
            <a
              href="mailto:info@trishivax.com"
              className="text-xs text-gray-600 hover:text-gold-400 transition-colors"
            >
              {t('footer.devContact')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
